import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';

const CustomReportsTab = () => {
  const [reportName, setReportName] = useState('');
  const [reportType, setReportType] = useState('placement');
  const [exportFormat, setExportFormat] = useState('pdf');
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [scheduleFrequency, setScheduleFrequency] = useState('none');

  const reportTypeOptions = [
    { value: 'placement', label: 'Placement Report' },
    { value: 'hiring-trends', label: 'Hiring Trends Report' },
    { value: 'employer-performance', label: 'Employer Performance Report' },
    { value: 'student-success', label: 'Student Success Report' },
    { value: 'custom', label: 'Custom Report' }
  ];

  const exportFormatOptions = [
    { value: 'pdf', label: 'PDF Document' },
    { value: 'excel', label: 'Excel Spreadsheet' },
    { value: 'csv', label: 'CSV File' },
    { value: 'json', label: 'JSON Data' }
  ];

  const scheduleOptions = [
    { value: 'none', label: 'No Schedule' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' }
  ];

  const availableMetrics = [
    { id: 'total-placements', label: 'Total Placements', category: 'Overview' },
    { id: 'placement-rate', label: 'Placement Rate', category: 'Overview' },
    { id: 'avg-package', label: 'Average Package', category: 'Salary' },
    { id: 'highest-package', label: 'Highest Package', category: 'Salary' },
    { id: 'hiring-velocity', label: 'Hiring Velocity', category: 'Trends' },
    { id: 'time-to-hire', label: 'Time to Hire', category: 'Trends' },
    { id: 'employer-satisfaction', label: 'Employer Satisfaction', category: 'Performance' },
    { id: 'retention-rate', label: 'Retention Rate', category: 'Performance' },
    { id: 'geographic-distribution', label: 'Geographic Distribution', category: 'Demographics' },
    { id: 'industry-breakdown', label: 'Industry Breakdown', category: 'Demographics' }
  ];

  const savedReports = [
    {
      id: 1,
      name: 'Monthly Placement Summary',
      type: 'Placement Report',
      lastGenerated: '2026-02-01',
      format: 'PDF',
      schedule: 'Monthly',
      status: 'Active'
    },
    {
      id: 2,
      name: 'Quarterly Hiring Trends',
      type: 'Hiring Trends Report',
      lastGenerated: '2026-01-15',
      format: 'Excel',
      schedule: 'Quarterly',
      status: 'Active'
    },
    {
      id: 3,
      name: 'Employer Performance Review',
      type: 'Employer Performance Report',
      lastGenerated: '2026-01-28',
      format: 'PDF',
      schedule: 'Weekly',
      status: 'Active'
    }
  ];

  const reportTemplates = [
    {
      id: 1,
      name: 'Executive Summary',
      description: 'High-level overview for leadership',
      metrics: ['total-placements', 'placement-rate', 'avg-package', 'employer-satisfaction'],
      icon: 'FileText'
    },
    {
      id: 2,
      name: 'Detailed Analytics',
      description: 'Comprehensive data analysis',
      metrics: ['total-placements', 'hiring-velocity', 'time-to-hire', 'retention-rate', 'geographic-distribution'],
      icon: 'BarChart3'
    },
    {
      id: 3,
      name: 'Employer Insights',
      description: 'Focused on employer metrics',
      metrics: ['employer-satisfaction', 'retention-rate', 'time-to-hire', 'hiring-velocity'],
      icon: 'Building2'
    }
  ];

  const handleMetricToggle = (metricId) => {
    setSelectedMetrics(prev => 
      prev?.includes(metricId) 
        ? prev?.filter(id => id !== metricId)
        : [...prev, metricId]
    );
  };

  const handleGenerateReport = () => {
    const reportData = {
      name: reportName || 'Untitled Report',
      type: reportType,
      format: exportFormat,
      metrics: selectedMetrics,
      generatedAt: new Date()?.toISOString()
    };

    if (exportFormat === 'json') {
      const dataStr = JSON.stringify(reportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportName || 'report'}-${Date.now()}.json`;
      link?.click();
      URL.revokeObjectURL(url);
    } else if (exportFormat === 'csv') {
      const csvContent = [
        ['Metric', 'Value'],
        ...selectedMetrics?.map(id => {
          const metric = availableMetrics?.find(m => m?.id === id);
          return [metric?.label || id, 'Sample Value'];
        })
      ]?.map(row => row?.join(','))?.join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportName || 'report'}-${Date.now()}.csv`;
      link?.click();
      URL.revokeObjectURL(url);
    } else {
      alert(`Generating ${exportFormat?.toUpperCase()} report: ${reportName || 'Untitled Report'}`);
    }
  };

  const handleLoadTemplate = (template) => {
    setReportName(template?.name);
    setSelectedMetrics(template?.metrics);
  };

  const handleScheduleReport = () => {
    if (scheduleFrequency !== 'none') {
      alert(`Report scheduled ${scheduleFrequency} with ${exportFormat?.toUpperCase()} format`);
    }
  };

  const groupedMetrics = availableMetrics?.reduce((acc, metric) => {
    if (!acc?.[metric?.category]) {
      acc[metric.category] = [];
    }
    acc?.[metric?.category]?.push(metric);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
            <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Report Builder</h3>
            
            <div className="space-y-4">
              <Input
                label="Report Name"
                placeholder="Enter report name"
                value={reportName}
                onChange={(e) => setReportName(e?.target?.value)}
              />

              <Select
                label="Report Type"
                options={reportTypeOptions}
                value={reportType}
                onChange={setReportType}
              />

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Select Metrics</label>
                <div className="space-y-3">
                  {Object.entries(groupedMetrics)?.map(([category, metrics]) => (
                    <div key={category} className="p-3 rounded-lg border border-border">
                      <h4 className="text-sm font-semibold text-card-foreground mb-2">{category}</h4>
                      <div className="space-y-2">
                        {metrics?.map((metric) => (
                          <div key={metric?.id} className="flex items-center gap-2">
                            <Checkbox
                              id={metric?.id}
                              checked={selectedMetrics?.includes(metric?.id)}
                              onCheckedChange={() => handleMetricToggle(metric?.id)}
                            />
                            <label htmlFor={metric?.id} className="text-sm text-card-foreground cursor-pointer">
                              {metric?.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Export Format"
                  options={exportFormatOptions}
                  value={exportFormat}
                  onChange={setExportFormat}
                />

                <Select
                  label="Schedule Frequency"
                  options={scheduleOptions}
                  value={scheduleFrequency}
                  onChange={setScheduleFrequency}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  variant="default" 
                  iconName="FileDown" 
                  onClick={handleGenerateReport}
                  disabled={selectedMetrics?.length === 0}
                >
                  Generate Report
                </Button>
                {scheduleFrequency !== 'none' && (
                  <Button 
                    variant="outline" 
                    iconName="Calendar" 
                    onClick={handleScheduleReport}
                  >
                    Schedule Report
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
            <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Saved Reports</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Report Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Last Generated</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Format</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Schedule</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {savedReports?.map((report) => (
                    <tr key={report?.id} className="border-b border-border hover:bg-muted/50 transition-smooth">
                      <td className="py-3 px-4 text-sm font-medium text-card-foreground">{report?.name}</td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">{report?.type}</td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">{report?.lastGenerated}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded">
                          {report?.format}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">{report?.schedule}</td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" iconName="Download">
                            Download
                          </Button>
                          <Button variant="ghost" size="sm" iconName="Edit">
                            Edit
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
            <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Report Templates</h3>
            <div className="space-y-3">
              {reportTemplates?.map((template) => (
                <div 
                  key={template?.id} 
                  className="p-4 rounded-lg border border-border hover:border-primary transition-smooth cursor-pointer"
                  onClick={() => handleLoadTemplate(template)}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name={template?.icon} size={20} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-card-foreground mb-1">{template?.name}</h4>
                      <p className="text-xs text-muted-foreground mb-2">{template?.description}</p>
                      <div className="flex items-center gap-1">
                        <Icon name="Layers" size={12} color="var(--color-muted-foreground)" />
                        <span className="text-xs text-muted-foreground">{template?.metrics?.length} metrics</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
            <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Export Options</h3>
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="FileText" size={16} color="var(--color-primary)" />
                  <span className="text-sm font-medium text-card-foreground">PDF</span>
                </div>
                <p className="text-xs text-muted-foreground">Professional formatted document</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="Table" size={16} color="var(--color-success)" />
                  <span className="text-sm font-medium text-card-foreground">Excel</span>
                </div>
                <p className="text-xs text-muted-foreground">Editable spreadsheet with formulas</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="FileSpreadsheet" size={16} color="var(--color-accent)" />
                  <span className="text-sm font-medium text-card-foreground">CSV</span>
                </div>
                <p className="text-xs text-muted-foreground">Raw data for analysis</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="Code" size={16} color="var(--color-warning)" />
                  <span className="text-sm font-medium text-card-foreground">JSON</span>
                </div>
                <p className="text-xs text-muted-foreground">Structured data format</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomReportsTab;